const KESA_QUOTE_VALID_DAYS = 30;
const KESA_QUOTE_STORAGE_KEY = 'kesa-quotation-draft-v1';

class KesaQuotationBuilder extends HTMLElement {
  constructor() {
    super();
    this.lines = [];
    this.searchAbort = null;
    this.currency = 'ZAR';
    this.formatter = new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: this.currency,
    });
    this.dateFormatter = new Intl.DateTimeFormat('en-ZA', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  connectedCallback() {
    this.cache();
    this.bind();
    this.setDefaults();
    this.restore();
    this.render();
  }

  cache() {
    this.form = this.querySelector('[data-quote-form]');
    this.results = this.querySelector('[data-product-results]');
    this.searchInput = this.querySelector('[data-product-search]');
    this.tbody = this.querySelector('[data-line-items]');
    this.table = this.querySelector('.kesa-quote__table');
    this.empty = this.querySelector('[data-empty-lines]');
    this.quoteNo = this.querySelector('[data-quote-no]');
    this.quoteDate = this.querySelector('[data-quote-date]');
    this.validUntil = this.querySelector('[data-valid-until]');
    this.clientOutput = this.querySelector('[data-client-output]');
    this.notesOutput = this.querySelector('[data-notes-output]');
    this.discountType = this.querySelector('[data-discount-type]');
    this.discountValue = this.querySelector('[data-discount-value]');
    this.totals = {
      gross: this.querySelector('[data-total-gross]'),
      grossRow: this.querySelector('[data-gross-row]'),
      lineDiscounts: this.querySelector('[data-total-line-discounts]'),
      lineDiscountRow: this.querySelector('[data-line-discount-row]'),
      subtotal: this.querySelector('[data-total-subtotal]'),
      discount: this.querySelector('[data-total-discount]'),
      discountRow: this.querySelector('[data-discount-row]'),
      total: this.querySelector('[data-total-grand]'),
    };
  }

  bind() {
    this.querySelector('[data-print-quote]')?.addEventListener('click', () => window.print());
    this.querySelector('[data-clear-quote]')?.addEventListener('click', () => this.clearQuote());
    this.querySelector('[data-add-custom]')?.addEventListener('click', () => this.addCustomLine());
    this.searchInput?.addEventListener('input', () => this.searchProducts());
    this.form?.addEventListener('input', () => this.render());
    this.tbody?.addEventListener('input', (event) => this.updateLine(event));
    this.tbody?.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (remove) this.removeLine(remove.dataset.removeLine);
    });

    // Valid-until tracks the quotation date until it is edited by hand.
    this.validUntil?.addEventListener('change', () => {
      this.validUntilPinned = true;
    });
    this.quoteDate?.addEventListener('change', () => {
      if (this.validUntilPinned) return;
      this.validUntil.value = this.toDateInput(this.addValidityWindow(this.quoteDate.value));
      this.render();
    });
  }

  addValidityWindow(from) {
    const base = from ? new Date(`${from}T00:00:00`) : new Date();
    const valid = new Date(Number.isNaN(base.getTime()) ? Date.now() : base.getTime());
    valid.setDate(valid.getDate() + KESA_QUOTE_VALID_DAYS);
    return valid;
  }

  setDefaults() {
    const today = new Date();
    const valid = this.addValidityWindow();
    this.validUntilPinned = false;

    if (this.quoteNo && !this.quoteNo.value) {
      const stamp = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, '0'),
        String(today.getDate()).padStart(2, '0'),
        String(today.getHours()).padStart(2, '0'),
        String(today.getMinutes()).padStart(2, '0'),
      ].join('');
      this.quoteNo.value = `KESA-${stamp}`;
    }

    if (this.quoteDate && !this.quoteDate.value) this.quoteDate.value = this.toDateInput(today);
    if (this.validUntil && !this.validUntil.value) this.validUntil.value = this.toDateInput(valid);
  }

  toDateInput(date) {
    // Local parts, not toISOString(): SAST is UTC+2, so a UTC slice would roll
    // the date back a day for anything created before 02:00.
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');
  }

  displayDate(value) {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? value : this.dateFormatter.format(date);
  }

  money(value) {
    return this.formatter.format(Number(value || 0));
  }

  number(value) {
    const parsed = Number(String(value || '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  async searchProducts() {
    const term = this.searchInput?.value.trim();
    if (!term || term.length < 2) {
      this.results.innerHTML = '';
      return;
    }

    if (this.searchAbort) this.searchAbort.abort();
    this.searchAbort = new AbortController();

    const url = `/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,vendor,variants.title`;

    try {
      const response = await fetch(url, { signal: this.searchAbort.signal });
      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      this.renderResults(products);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.results.innerHTML = '<p class="kesa-quote__empty">Product search is unavailable. Add a custom product instead.</p>';
      }
    }
  }

  renderResults(products) {
    if (!products.length) {
      this.results.innerHTML = '<p class="kesa-quote__empty">No matching products found. You can add a custom product.</p>';
      return;
    }

    this.results.innerHTML = products
      .map((product) => {
        const image = product.image || product.featured_image?.url || '';
        const handle = product.handle || this.handleFromUrl(product.url);
        return `
          <button type="button" class="kesa-quote__result" data-product-handle="${this.escape(handle)}">
            <span>${image ? `<img src="${this.escape(image)}" alt="">` : ''}</span>
            <span>
              <strong>${this.escape(product.title || 'Product')}</strong>
              <small>${this.escape(product.vendor || product.type || 'Website product')}</small>
            </span>
            <span>Add</span>
          </button>
        `;
      })
      .join('');

    this.results.querySelectorAll('[data-product-handle]').forEach((button) => {
      button.addEventListener('click', () => this.addProduct(button.dataset.productHandle));
    });
  }

  handleFromUrl(url) {
    return String(url || '').split('/products/')[1]?.split('?')[0]?.replace(/^\/+|\/+$/g, '') || '';
  }

  async addProduct(handle) {
    if (!handle) return;
    const response = await fetch(`/products/${handle}.js`);
    const product = await response.json();
    const variant = product.variants?.find((item) => item.available) || product.variants?.[0] || {};
    const price = Number(variant.price || product.price || 0) / 100;

    this.lines.push({
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      title: product.title || 'Website product',
      url: product.url || `/products/${handle}`,
      quantity: 1,
      unitPrice: price,
      discountPercent: 0,
      note: variant.title && variant.title !== 'Default Title' ? variant.title : '',
    });

    if (this.searchInput) this.searchInput.value = '';
    if (this.results) this.results.innerHTML = '';
    this.render();
  }

  addCustomLine() {
    this.lines.push({
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      title: 'Custom product',
      url: '',
      quantity: 1,
      unitPrice: 0,
      discountPercent: 0,
      note: '',
    });
    this.render();
  }

  removeLine(id) {
    this.lines = this.lines.filter((line) => line.id !== id);
    this.render();
  }

  updateLine(event) {
    const input = event.target;
    const row = input.closest('[data-line-id]');
    if (!row) return;

    const line = this.lines.find((item) => item.id === row.dataset.lineId);
    if (!line) return;

    const field = input.dataset.field;
    if (!field) return;

    if (['quantity', 'unitPrice'].includes(field)) {
      line[field] = this.number(input.value);
    } else if (field === 'discountPercent') {
      line[field] = Math.min(100, Math.max(0, this.number(input.value)));
    } else {
      line[field] = input.value;
    }

    this.renderTotals();
    row.querySelector('[data-line-total]').textContent = this.money(this.lineTotal(line));
    this.persist();
  }

  lineGross(line) {
    return Math.max(0, this.number(line.quantity) * this.number(line.unitPrice));
  }

  lineDiscount(line) {
    const percent = Math.min(100, Math.max(0, this.number(line.discountPercent)));
    return this.lineGross(line) * (percent / 100);
  }

  lineTotal(line) {
    return Math.max(0, this.lineGross(line) - this.lineDiscount(line));
  }

  generalDiscount(subtotal) {
    const type = this.discountType?.value || 'none';
    const value = this.number(this.discountValue?.value);
    if (value <= 0 || type === 'none') return 0;
    if (type === 'percent') return Math.min(subtotal, subtotal * (value / 100));
    if (type === 'amount') return Math.min(subtotal, value);
    return 0;
  }

  totalsValue() {
    const gross = this.lines.reduce((sum, line) => sum + this.lineGross(line), 0);
    const lineDiscounts = this.lines.reduce((sum, line) => sum + this.lineDiscount(line), 0);
    const subtotal = Math.max(0, gross - lineDiscounts);
    const discount = this.generalDiscount(subtotal);
    return {
      gross,
      lineDiscounts,
      subtotal,
      discount,
      total: Math.max(0, subtotal - discount),
    };
  }

  render() {
    this.renderMeta();
    this.renderLines();
    this.renderTotals();
    this.persist();
  }

  // Draft autosave. A quotation can take a while to put together and a stray
  // refresh should not throw the work away. Local to this browser only.
  persist() {
    if (!this.form) return;
    try {
      const fields = {};
      new FormData(this.form).forEach((value, key) => {
        fields[key] = value;
      });
      localStorage.setItem(KESA_QUOTE_STORAGE_KEY, JSON.stringify({
        fields,
        lines: this.lines,
        validUntilPinned: this.validUntilPinned === true,
      }));
    } catch (error) {
      /* storage unavailable or full — the builder still works, just without autosave */
    }
  }

  restore() {
    let saved;
    try {
      saved = JSON.parse(localStorage.getItem(KESA_QUOTE_STORAGE_KEY) || 'null');
    } catch (error) {
      saved = null;
    }
    if (!saved || typeof saved !== 'object') return;

    if (Array.isArray(saved.lines)) {
      this.lines = saved.lines.map((line) => ({
        id: line.id || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`),
        title: String(line.title || 'Product'),
        url: String(line.url || ''),
        quantity: this.number(line.quantity),
        unitPrice: this.number(line.unitPrice),
        discountPercent: Math.min(100, Math.max(0, this.number(line.discountPercent))),
        note: String(line.note || ''),
      }));
    }

    if (saved.fields && this.form) {
      Object.entries(saved.fields).forEach(([key, value]) => {
        const field = this.form.elements[key];
        if (field && typeof value === 'string') field.value = value;
      });
    }

    this.validUntilPinned = saved.validUntilPinned === true;
  }

  renderMeta() {
    const data = new FormData(this.form);
    const client = [
      data.get('client_name'),
      data.get('client_company'),
      data.get('client_email'),
      data.get('client_phone'),
      data.get('client_address'),
    ].filter(Boolean);
    this.clientOutput.innerHTML = client.length
      ? client.map((item) => `<div>${this.escape(item)}</div>`).join('')
      : '<div>Client details will appear here.</div>';

    const notes = data.get('quote_notes');
    this.notesOutput.textContent = notes || 'No additional notes.';

    this.querySelector('[data-output-quote-no]').textContent = data.get('quote_no') || '-';
    this.querySelector('[data-output-date]').textContent = this.displayDate(data.get('quote_date'));
    this.querySelector('[data-output-valid]').textContent = this.displayDate(data.get('valid_until'));
    this.querySelector('[data-output-prepared]').textContent = data.get('prepared_by') || 'Kitchen Equip SA';
  }

  renderLines() {
    if (!this.lines.length) {
      this.tbody.innerHTML = '';
      this.empty.hidden = false;
      return;
    }

    this.empty.hidden = true;
    this.tbody.innerHTML = this.lines
      .map((line, index) => `
        <tr data-line-id="${this.escape(line.id)}">
          <td>${index + 1}</td>
          <td>
            <input data-field="title" value="${this.escape(line.title)}" aria-label="Product description">
            <input data-field="note" value="${this.escape(line.note)}" aria-label="Line note" placeholder="Optional model, size or note">
          </td>
          <td><input class="kesa-quote__number" type="number" min="0" step="1" data-field="quantity" value="${this.escape(line.quantity)}" aria-label="Quantity"></td>
          <td><input class="kesa-quote__number" type="number" min="0" step="0.01" data-field="unitPrice" value="${this.escape(line.unitPrice)}" aria-label="Unit price"></td>
          <td><input class="kesa-quote__number" type="number" min="0" max="100" step="0.5" data-field="discountPercent" value="${this.escape(line.discountPercent || 0)}" aria-label="Line discount percentage"></td>
          <td class="kesa-quote__number" data-line-total>${this.money(this.lineTotal(line))}</td>
          <td><button type="button" class="kesa-quote__remove" data-remove-line="${this.escape(line.id)}">Remove</button></td>
        </tr>
      `)
      .join('');
  }

  renderTotals() {
    const totals = this.totalsValue();
    this.totals.subtotal.textContent = this.money(totals.subtotal);
    this.totals.discount.textContent = `-${this.money(totals.discount)}`;
    this.totals.total.textContent = this.money(totals.total);
    if (this.totals.discountRow) this.totals.discountRow.hidden = totals.discount <= 0;

    this.table?.classList.toggle('is-discounted', totals.lineDiscounts > 0);

    if (this.totals.gross) this.totals.gross.textContent = this.money(totals.gross);
    if (this.totals.lineDiscounts) this.totals.lineDiscounts.textContent = `-${this.money(totals.lineDiscounts)}`;
    if (this.totals.lineDiscountRow) this.totals.lineDiscountRow.hidden = totals.lineDiscounts <= 0;
    if (this.totals.grossRow) this.totals.grossRow.hidden = totals.lineDiscounts <= 0;
  }

  clearQuote() {
    if (!confirm('Clear this quotation and start again?')) return;
    this.lines = [];
    this.form.reset();
    if (this.quoteNo) this.quoteNo.value = '';
    if (this.quoteDate) this.quoteDate.value = '';
    if (this.validUntil) this.validUntil.value = '';
    try {
      localStorage.removeItem(KESA_QUOTE_STORAGE_KEY);
    } catch (error) {
      /* nothing to clear */
    }
    this.setDefaults();
    this.render();
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('kesa-quotation-builder', KesaQuotationBuilder);
